import React, { useEffect, useState } from 'react';
import {
  GoogleAccount,
  GoogleSheet,
  GoogleSpreadsheet,
} from '@mm/state/interfaces/googleSheet';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { useSelector } from 'react-redux';
import {
  selectUserGoogleAccountsWithSheetSupport,
  selectSheets,
  selectSpreadsheets,
} from '@mm/state/selectors/googleSheetSelectors';
import { Button, Spinner } from '@mm/components';
import PrimarySelect, {
  DropdownOption,
} from '../../components/PrimarySelect/PrimarySelect';
import { useHistory } from 'react-router-dom';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { landmineNames } from '@mm/constants/landmine-names';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import classNames from 'classnames';
import { CloseOutlined } from '@ant-design/icons';
import { IGoogleSpreadsheetModal } from '@mm/state/modules/modalsState/props';
import css from './GoogleSpreadsheetModal.module.css';

export const ConnectAccountBtn = ({
  oauthUrl: propOauthUrl = '',
  className = '',
  onClick = () => {},
  children = 'Add an Account',
  ...props
}) => {
  const history = useHistory();
  const [oauthUrl, setOauthUrl] = useState(propOauthUrl);

  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const showUpgradeModal = onSuccessBilling =>
    openUniversalUpgradeLandmineModal({
      feature: 'nativeIntegrations',
      planFallback: PageBillingType.ProUnicorn,
      landmineName: landmineNames.INTEGRATIONS_CONTACT_SUPPRESSION,
      onSuccessBilling,
    });

  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;

  const { getOauthUrl } = useGoogleSheetsCRUDActions();

  const onConnectAccountClick = e => {
    if (!hasNativeIntegrationsFeature) {
      e.stopPropagation();
      showUpgradeModal(() => history.push(oauthUrl));
    } else {
      window.open(oauthUrl, '_self');
    }
  };

  useEffect(() => {
    const getLink = async () => {
      const link = await getOauthUrl(window.location.href);
      if (link) {
        setOauthUrl(link);
      }
    };

    if (!oauthUrl) {
      getLink();
    }
  }, []);

  return (
    <Button
      css={{ container: className }}
      onClick={onConnectAccountClick}
      {...props}
    >
      {children}
    </Button>
  );
};

export const GoogleSpreadsheetModal: React.FC<IGoogleSpreadsheetModal> = ({
  className,
  onReject = () => {},
  onSubmit = async () => {},
}) => {
  const [selectedAccount, setSelectedAccount] = useState<GoogleAccount>();

  const [busy, setBusy] = useState(false);

  const { getSpreadsheets, getSheets } = useGoogleSheetsCRUDActions();

  const availableAccounts = useSelector(
    selectUserGoogleAccountsWithSheetSupport,
  );

  const spreadsheets = useSelector(selectSpreadsheets);
  const sheets = useSelector(selectSheets);
  const [selectedSpreadsheet, setSelectedSpreadsheet] =
    useState<GoogleSpreadsheet>();
  const [selectedSheet, setSelectedSheet] = useState<GoogleSheet>();

  const onSelectSheet = async (item: GoogleSheet) => {
    setSelectedSheet(item);
  };

  const onSelectSpreadsheet = (item: GoogleSpreadsheet) => {
    setSelectedSpreadsheet(item);
    if (selectedAccount) {
      getSheets(selectedAccount.id, item.id);
    }
  };

  const onSelectAccount = (item: GoogleAccount) => {
    setSelectedAccount(item);
    getSpreadsheets(item.id);
  };

  const isReadOnlyAccount = false;

  const googleSpreadsheetTitle = '';
  const googleSheetTitle = '';

  const isSubmitDisabled = !selectedSheet || busy;

  const handleReject = () => onReject();
  const handleSubmit = async () => {
    if (selectedSheet && selectedSpreadsheet && selectedAccount) {
      setBusy(true);
      try {
        await onSubmit({
          sheet: selectedSheet,
          spreadsheet: selectedSpreadsheet,
          account: selectedAccount,
        });
      } finally {
        setBusy(false);
      }
    }
  };

  return (
    <div className={classNames(className, css.container)}>
      <div className={classNames(css.modal, { [css.error]: false })}>
        <div className={css.modalContent}>
          <span className={css.close} onClick={handleReject}>
            <CloseOutlined />
          </span>
          <h1 className={css.title}>
            Add Google Sheet
            <span className={css.subtitle}>Set up your worksheet</span>
          </h1>

          <div className={css.blocks}>
            <div className={css.dropdown}>
              <div className={css.fieldNameRow}>
                <span className={css.attention}>Choose an account</span>
                <ConnectAccountBtn
                  color="transparent-no-shadow"
                  className={css.addAccountBtn}
                />
              </div>

              <PrimarySelect
                className={css.primarySelect}
                activeItemClassName={css.activeItemSelect}
                placeholder="Account"
                options={availableAccounts}
                value={selectedAccount}
                onChange={item => onSelectAccount(item as GoogleAccount)}
                displayNameKey="email"
                dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
                customDropdownStyles={{ zIndex: '2147483647' }}
                disabled={isReadOnlyAccount}
              />
            </div>

            <div className={css.dropdown}>
              <span className={css.attention}>Choose Spreadsheet</span>
              <PrimarySelect
                className={css.primarySelect}
                activeItemClassName={css.activeItemSelect}
                placeholder={
                  googleSpreadsheetTitle
                    ? googleSpreadsheetTitle
                    : 'Spreadsheet'
                }
                options={spreadsheets}
                value={selectedSpreadsheet}
                onChange={item =>
                  onSelectSpreadsheet(item as GoogleSpreadsheet)
                }
                dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
                disabled={isReadOnlyAccount}
                customDropdownStyles={{ zIndex: '2147483647' }}
              />
            </div>

            <div className={css.dropdown}>
              <span className={css.attention}>Choose a Worksheet</span>
              <PrimarySelect
                className={css.primarySelect}
                activeItemClassName={css.activeItemSelect}
                placeholder={googleSheetTitle ? googleSheetTitle : 'Worksheet'}
                displayNameKey="title"
                options={sheets}
                value={selectedSheet as DropdownOption}
                onChange={item => onSelectSheet(item as unknown as GoogleSheet)}
                dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
                disabled={isReadOnlyAccount}
                customDropdownStyles={{ zIndex: '2147483647' }}
              />
            </div>
          </div>

          <div className={css.actions}>
            <Button
              color="transparent-no-shadow"
              onClick={handleReject}
              size="lg"
              css={{ container: css.cancelButton }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              size="lg"
              disabled={isSubmitDisabled}
              rightIcon={
                busy && (
                  <Spinner
                    show={busy}
                    position="relative"
                    size={13}
                    color="white"
                    backgroundColor="transparent"
                  />
                )
              }
            >
              Connect
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
